import type { GA4 } from "react-ga4/types/ga4";

/**
 * Attribution query param names safe to keep (case-insensitive).
 *
 * A similar allowlist exists in discovery's and mojo.website's
 * `static-global.tsx` - reconcile all three when adding a new param.
 */
const ALLOWLISTED_PARAM_NAMES = new Set([
  "gclid",
  "gbraid",
  "wbraid",
  "gclsrc",
  "dclid",
  "gad_source",
  "gad_campaignid",
  "msclkid",
  "fbclid",
  "ttclid",
  "twclid",
  "li_fat_id",
  "matchtype",
  "network",
  "adposition",
  "adgroupid",
  "partnertrackingid",
  "device",
  "ref",
]);

/**
 * Query param name prefixes (case-insensitive) that are safe to keep as they
 * are used for marketing attribution and don't carry PII
 */
const ALLOWLISTED_PARAM_PREFIXES = ["utm_", "sfmc_"];

/**
 * Whether a query param name is allowed to be kept on a cleaned URL
 */
const isAllowlistedParamName = (name: string): boolean => {
  const lowerName = name.toLowerCase();

  return (
    ALLOWLISTED_PARAM_NAMES.has(lowerName)
    || ALLOWLISTED_PARAM_PREFIXES.some((prefix) => lowerName.startsWith(prefix))
  );
};

/**
 * Removes PII from a URL using a fail-safe allowlist: only known marketing attribution
 * params survive (see ALLOWLISTED_PARAM_NAMES/PREFIXES), everything else is stripped.
 * Returns the URL unchanged if it can't be parsed, so it's safe to use on any GA data.
 */
export const cleanPiiUrl = (value: string): string => {
  try {
    const decodedString = decodeURIComponent(value);
    const url = new URL(decodedString);

    for (const name of Array.from(url.searchParams.keys())) {
      if (!isAllowlistedParamName(name)) url.searchParams.delete(name);
    }

    return url.href;
  } catch (_) {
    return value; // Don't stop GA from sending data, just carry on as normal
  }
};

/**
 * Cleans all PII from GA data before sending, by monkey-patching react-ga4's internal
 * `_gtag` to intercept and strip the payload before it's sent.
 * A patch isn't ideal, but it's the only way to guarantee every event is cleaned.
 */
export const interceptPii = <T extends GA4>(reactGA4: T): T => {
  const originalGtag = reactGA4._gtag.bind(reactGA4);

  // Defined once per `interceptPii()` call, not per `_gtag` call.
  const cleanPiiFields = (payload: Record<string, any>) => {
    const cleanParams = { ...payload };

    if ("page_location" in cleanParams)
      cleanParams.page_location = cleanPiiUrl(cleanParams.page_location);
    if ("page_referrer" in cleanParams)
      cleanParams.page_referrer = cleanPiiUrl(cleanParams.page_referrer);
    if ("full_page_url" in cleanParams)
      cleanParams.full_page_url = cleanPiiUrl(cleanParams.full_page_url);
    // Also cleans `useTrackPageView`'s page URL; bare paths (no origin) pass through cleanPiiUrl unchanged.
    if ("simplified_url" in cleanParams)
      cleanParams.simplified_url = cleanPiiUrl(cleanParams.simplified_url);

    return cleanParams;
  };

  reactGA4._gtag = (command: string, action: string | Record<string, any>, ...args: any[]) => {
    if (command === "set" && args.length === 0 && typeof action === "object") {
      // .set(fieldsObject) has only 2 args, so the payload lands in `action`, not args[0].
      action = cleanPiiFields(action);
    } else if (args.length > 0) {
      const cleanParams = cleanPiiFields(args[0]);

      if (command === "config" && typeof document !== "undefined" && document.referrer) {
        // Override any auto-collected page_referrer with our cleaned one.
        cleanParams.page_referrer = cleanPiiUrl(document.referrer);
      }

      args[0] = cleanParams;
    }

    originalGtag(command, action, ...args);
  };

  // Needs `window` for `page_location`, which isn't available at Next.js build time.
  if (typeof window !== "undefined") {
    const cleanLocation = cleanPiiUrl(window.location.href);
    const cleanReferrer = cleanPiiUrl(document.referrer);
    reactGA4.set({
      page_location: cleanLocation,
      page_referrer: cleanReferrer,
    });
  }

  return reactGA4;
};
